import type { BlogStatus, SocialStatus } from "../lib/api";

export interface Stage<T extends string> {
  key: T;
  label: string;
  hint: string;
}

/**
 * Visual stages for the blog pipeline. Each corresponds 1:1 to a `blogs.status`
 * enum value; terminal states (failed, rejected) are handled separately.
 */
export const BLOG_STAGES: Stage<BlogStatus>[] = [
  { key: "researching", label: "Research", hint: "Pulling sources and outlining the topic." },
  { key: "drafting", label: "Draft", hint: "Writing the body — usually about a minute." },
  { key: "keywording", label: "Keywords", hint: "Weaving target keywords into the copy." },
  { key: "imaging", label: "Image", hint: "Generating the feature image — usually about 2 minutes." },
  { key: "reviewing", label: "Review", hint: "Running safety and quality checks." },
  { key: "awaiting_approval", label: "Your review", hint: "Ready for you to approve." },
  { key: "approved", label: "Approved", hint: "Ready to publish to WordPress." },
  { key: "published", label: "Published", hint: "Live on the site." },
];

/**
 * Visual stages for a social post. "approved" is collapsed into "scheduled"
 * because the backend transitions them within seconds of each other.
 */
export const SOCIAL_STAGES: Stage<SocialStatus>[] = [
  { key: "drafting", label: "Caption", hint: "Writing the caption." },
  { key: "imaging", label: "Image", hint: "Generating the image." },
  { key: "reviewing", label: "Review", hint: "Running safety and quality checks." },
  { key: "awaiting_approval", label: "Your review", hint: "Ready for you to approve." },
  { key: "scheduled", label: "Scheduled", hint: "Waiting for its publish time." },
  { key: "published", label: "Published", hint: "Posted to Facebook and Instagram." },
];

export type TerminalState = "failed" | "rejected" | null;

export function blogStageIndex(status: BlogStatus): number {
  const i = BLOG_STAGES.findIndex((s) => s.key === status);
  return i;
}

export function socialStageIndex(status: SocialStatus): number {
  if (status === "approved") {
    return SOCIAL_STAGES.findIndex((s) => s.key === "scheduled");
  }
  return SOCIAL_STAGES.findIndex((s) => s.key === status);
}

export function blogTerminal(status: BlogStatus): TerminalState {
  if (status === "failed") return "failed";
  if (status === "rejected") return "rejected";
  return null;
}

export function socialTerminal(status: SocialStatus): TerminalState {
  if (status === "failed") return "failed";
  if (status === "rejected") return "rejected";
  return null;
}
